import { ChangeService } from "@/@types/changeservice";
import { createContext, Dispatch } from "react";

export enum Choice {
  "YES" = "YES",
  "NO" = "NO",
}

export enum Salutation {
  "MR" = "Herr",
  "MS" = "Frau",
}

export enum Title {
  "NONE" = "",
  "DR" = "Doktor",
  "PROF" = "Professor",
}

export enum ChangeDateSelection {
  "NEXT_POSSIBLE_DATE" = "NEXT_POSSIBLE_DATE",
  "CUSTOM_SWITCHING_DATE" = "CUSTOM_SWITCHING_DATE",
  "CANCELLED_ALREADY" = "CANCELLED_ALREADY",
}

export enum Reason {
  "SWITCH_PROVIDER" = "SWITCH_PROVIDER",
  "FIRST_OCCUPANCY" = "FIRST_OCCUPANCY",
  "RELOCATION" = "RELOCATION",
}

export enum Types {
  "INITIALIZE" = "INITIALIZE",
  "UPDATE_HAS_CHANGED" = "UPDATE_HAS_CHANGED",
  "UPDATE_ID" = "UPDATE_ID",
  "UPDATE_ENTITY" = "UPDATE_ENTITY",
  "SIGNATURE_CHANGED" = "SIGNATURE_CHANGED",
}

export enum PropertyTypes {
  "HOUSE" = "HOUSE",
  "APT" = "APT",
  "COMPANY" = "COMPANY",
}

export interface State {
  hasChanged: boolean;
  id?: string;
  salutation: Salutation;
  title: Title;
  firstname: { value: string; error?: string };
  lastname: { value: string; error?: string };
  birthday: { value: [string, string, string]; error?: string };
  phonenumber: { value: string; error?: string };
  signature: { value: string; error?: string };
  tmpSignature: { value?: string; error?: string };
  counterNumber: { value?: string; error?: string; csCheck: { checked: boolean; csId: string } };
  postalcode: { value?: string; error?: string };
  location: {
    id: string;
    value?: string;
    error?: string;
    locations: { id: number; mainName: string; name: string; partName: string }[];
  };
  street: { value?: string; error?: string };
  streetNumber: { value?: string; error?: string };
  companyName: { value?: string; error?: string };
  taxId: { value?: string; error?: string };
  legalForm: { value?: string; error?: string };
  sameAsDelivery: Choice;
  differentSalutation?: Salutation;
  differentTitle?: Title;
  differentFirstname?: { value: string; error?: string };
  differentLastname?: { value: string; error?: string };
  differentCompanyName?: { value: string; error?: string };
  differentTaxId?: { value: string; error?: string };
  differentBirthday?: { value: [string, string, string]; error?: string };
  differentPostalcode?: { value?: string; error?: string };
  differentLocation?: {
    id: string;
    value?: string;
    error?: string;
    locations: { id: number; mainName: string; name: string; partName: string }[];
  };
  differentStreet?: { value?: string; error?: string };
  differentStreetNumber?: { value?: string; error?: string };
  consumption: { value: string; error?: string };
  consumptionNt?: { value: string; error?: string };
  provider: {
    categoryId?: string;
    providerName?: string;
    categoryProviderId?: string;
  };
  customerNumber?: { value?: string; error?: string };
  changeDate: {
    value?: ChangeDateSelection;
    dateTmp: [string, string, string];
    date: [string, string, string];
    error?: string;
  };
  reason?: Reason;
  eco: Choice;
  iban?: { value?: string; error?: string };
  ibanOld: { value: string; error: undefined };
  ibanNew: { value: string; error: undefined };
  ibanNewRepeat: { value: string; error: undefined };
  differentAccountHolder: Choice;
  differentAccountHolderSalutation?: Salutation;
  differentAccountHolderTitle?: Title;
  differentAccountHolderFirstname?: { value: string; error?: string };
  differentAccountHolderLastname?: { value: string; error?: string };
  differentAccountHolderCompanyName?: { value: string; error?: string };
  differentAccountHolderTaxId?: { value: string; error?: string };
  differentAccountHolderBirthday?: { value: [string, string, string]; error?: string };
  differentAccountHolderPostalcode?: { value?: string; error?: string };
  differentAccountHolderLocation?: {
    id: string;
    value?: string;
    error?: string;
    locations: { id: number; mainName: string; name: string; partName: string }[];
  };
  differentAccountHolderStreet?: { value?: string; error?: string };
  differentAccountHolderStreetNumber?: { value?: string; error?: string };
  propertyType: PropertyTypes;
  bankHttpError?: string;
}

interface Action {
  type: Types;
  payload?:
    | number
    | boolean
    | string
    | undefined
    | Record<
        string,
        | string
        | undefined
        | [string, string, string]
        | { id: number; mainName: string; name: string; partName: string }[]
        | { checked: boolean; csId: string }
      >
    | State;
  key?: string;
  keepSignature?: boolean;
}

export const updateContext = (state: State, action: Action) => {
  switch (action.type) {
    case Types.UPDATE_HAS_CHANGED:
      return { ...structuredClone(state), hasChanged: action.payload as boolean };
    case Types.UPDATE_ID:
      return { ...structuredClone(state), id: action.payload as string };
    case Types.UPDATE_ENTITY:
      return action.key
        ? {
            ...structuredClone(state),
            ...{
              [action.key]:
                typeof action.payload === "object"
                  ? {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      ...structuredClone(state[action.key]),
                      ...action.payload,
                    }
                  : action.payload,
            },
          }
        : state;
    case Types.INITIALIZE:
      const result = { ...(action.payload as State) };
      if (action.keepSignature) {
        result.signature = { ...structuredClone(state.signature) };
        result.tmpSignature = { ...structuredClone(state.tmpSignature) };
      }
      return result;
    case Types.SIGNATURE_CHANGED:
      return { ...structuredClone(state), signatureChanged: action.payload as boolean };
  }
};

export const CSEditContextState = (changeService?: ChangeService) => {
  let changeDate = undefined;
  if (
    changeService?.switchTariffDateType === "NONE" ||
    changeService?.cancellationContractType === "CANCELLED_ALREADY"
  ) {
    changeDate = changeService?.canceledMyselfDate;
  } else if (changeService?.switchTariffDateType === "CUSTOM_SWITCHING_DATE") {
    changeDate = changeService?.desiredProviderChangeDate;
  }

  const parsedChangeDate = changeDate
    ? (Intl.DateTimeFormat("DE-de", { year: "numeric", month: "2-digit", day: "2-digit" })
        .format(new Date(changeDate))
        .split(".") as [string, string, string])
    : (["", "", ""] as [string, string, string]);

  return {
    hasChanged: false,
    id: changeService?.id,
    salutation: (changeService?.salutation as Salutation) || Salutation.MR,
    title: (changeService?.salutationTitle as Title) || Title.NONE,
    firstname: { value: changeService?.firstname || "", error: undefined },
    lastname: { value: changeService?.lastname || "", error: undefined },
    companyName: { value: changeService?.companyName || "", error: undefined },
    taxId: { value: changeService?.taxId || "", error: undefined },
    legalForm: { value: changeService?.legalForm || "", error: undefined },
    birthday: {
      value: changeService?.dateOfBirth
        ? (Intl.DateTimeFormat("DE-de", { year: "numeric", month: "2-digit", day: "2-digit" })
            .format(new Date(changeService?.dateOfBirth))
            .split(".") as [string, string, string])
        : (["", "", ""] as [string, string, string]),
      error: undefined,
    },
    phonenumber: {
      value: changeService?.formattedE164PhoneNumber ? changeService?.formattedE164PhoneNumber.replace("+49", "") : "",
      error: undefined,
    },
    signature: { value: "", error: undefined },
    tmpSignature: { value: undefined, error: undefined },
    counterNumber: { value: changeService?.counterNumber, error: undefined, csCheck: { checked: false, csId: "" } },
    postalcode: { value: changeService?.postalcode, error: undefined },
    location: {
      value: changeService?.location,
      error: "",
      id: changeService?.locationId?.toString() || "",
      locations: [],
    },
    street: { value: changeService?.street || "", error: undefined },
    streetNumber: { value: changeService?.streetNumber, error: undefined },
    sameAsDelivery: changeService?.billingAddressSameAsDeliveryAddress ? Choice.YES : Choice.NO || Choice.YES,
    differentSalutation: (changeService?.differentSalutation as Salutation) || Salutation.MR,
    differentTitle: (changeService?.differentSalutationTitle as Title) || Title.NONE,
    differentFirstname: { value: changeService?.differentFirstname || "", error: undefined },
    differentLastname: { value: changeService?.differentLastname || "", error: undefined },
    differentCompanyName: { value: changeService?.differentCompanyName || "", error: undefined },
    differentTaxId: { value: changeService?.differentTaxId || "", error: undefined },
    differentBirthday: {
      value: changeService?.differentDateOfBirth
        ? (Intl.DateTimeFormat("DE-de", { year: "numeric", month: "2-digit", day: "2-digit" })
            .format(new Date(changeService?.differentDateOfBirth))
            .split(".") as [string, string, string])
        : (["", "", ""] as [string, string, string]),
      error: undefined,
    },
    differentPostalcode: { value: changeService?.differentPostalcode || "", error: undefined },
    differentLocation: {
      value: changeService?.differentLocation,
      error: "",
      id: changeService?.differentLocationId?.toString() || "",
      locations: [],
    },
    differentStreet: { value: changeService?.differentStreet || "", error: undefined },
    differentStreetNumber: { value: changeService?.differentStreetNumber, error: undefined },
    consumption: {
      value:
        (changeService?.switchProviderOrMove === Reason.SWITCH_PROVIDER
          ? changeService?.currentConsumption?.toString()
          : changeService?.expectedConsumption?.toString()) || "",
      error: undefined,
    },
    consumptionNt: { value: changeService?.consumptionNt?.toString() || "", error: undefined },
    provider: {
      categoryId: changeService?.categoryId,
      providerName: changeService?.currentProviderName || undefined,
      categoryProviderId: changeService?.categoryProviderId || undefined,
    },
    customerNumber: { value: changeService?.customerNumber, error: undefined },
    changeDate: {
      value:
        changeService?.switchTariffDateType === "NONE"
          ? ChangeDateSelection.CANCELLED_ALREADY
          : changeService?.switchTariffDateType,
      dateTmp: parsedChangeDate,
      date: parsedChangeDate,
      error: undefined,
    },
    reason: (changeService?.switchProviderOrMove === Reason.SWITCH_PROVIDER
      ? Reason.SWITCH_PROVIDER
      : changeService?.firstOccupancy) as Reason,
    eco: changeService?.ecoEnergy ? Choice.YES : Choice.NO,
    iban: { value: changeService?.accountIban, error: undefined },
    ibanOld: { value: "", error: undefined },
    ibanNew: { value: "", error: undefined },
    ibanNewRepeat: { value: "", error: undefined },
    differentAccountHolder: changeService?.accountOwnerType === "IMPORT_FROM_DELIVERY_ADDRESS" ? Choice.NO : Choice.YES,
    differentAccountHolderSalutation: (changeService?.differentAccountHolderSalutation as Salutation) || Salutation.MR,
    differentAccountHolderTitle: (changeService?.differentAccountHolderSalutationTitle as Title) || Title.NONE,
    differentAccountHolderFirstname: { value: changeService?.differentAccountHolderFirstname || "", error: undefined },
    differentAccountHolderLastname: { value: changeService?.differentAccountHolderLastname || "", error: undefined },
    differentAccountHolderCompanyName: {
      value: changeService?.differentAccountHolderCompanyName || "",
      error: undefined,
    },
    differentAccountHolderTaxId: { value: changeService?.differentAccountHolderTaxId || "", error: undefined },
    differentAccountHolderBirthday: {
      value: changeService?.differentAccountHolderDateOfBirth
        ? (Intl.DateTimeFormat("DE-de", { year: "numeric", month: "2-digit", day: "2-digit" })
            .format(new Date(changeService?.differentAccountHolderDateOfBirth))
            .split(".") as [string, string, string])
        : (["", "", ""] as [string, string, string]),
      error: undefined,
    },
    differentAccountHolderPostalcode: {
      value: changeService?.differentAccountHolderPostalcode || "",
      error: undefined,
    },
    differentAccountHolderLocation: {
      value: changeService?.differentAccountHolderLocation,
      error: "",
      id: changeService?.differentAccountHolderLocationId?.toString() || "",
      locations: [],
    },
    differentAccountHolderStreet: { value: changeService?.differentAccountHolderStreet || "", error: undefined },
    differentAccountHolderStreetNumber: { value: changeService?.differentAccountHolderStreetNumber, error: undefined },
    propertyType: changeService?.propertyType as PropertyTypes,
  };
};

type Context = [State, Dispatch<Action>];

export const CSEdit = createContext<Context>([CSEditContextState(), () => null]);
